import { Controller } from "@hotwired/stimulus"
import { bindClickOutside } from "../utils/click_outside"
import { handleListKeydown } from "../utils/keyboard_nav"

/**
 * Tailwind-native role autocomplete for the SalaryLab calculator.
 * Searches job roles from pre-loaded JSON and dispatches selection events.
 */
export default class extends Controller {
  static targets = ["input", "results", "rolesData", "clearButton"]

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.filteredRoles = []
    this.roles = []
    this.selectedRole = null

    if (this.hasRolesDataTarget) {
      try {
        this.roles = JSON.parse(this.rolesDataTarget.textContent)
      } catch (e) {
        // Roles data not available
      }
    }

    this.unbindClickOutside = bindClickOutside(this.element, () => this.closeResults())
  }

  disconnect() {
    if (this.unbindClickOutside) this.unbindClickOutside()
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.closeResults()
      return
    }

    this.filteredRoles = this._filterRoles(query).slice(0, 8)
    this._renderResults()
    this._openResults()
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const role = this.filteredRoles[index]
    if (role) this._selectRole(role)
  }

  clear() {
    this.inputTarget.value = ""
    this.selectedRole = null
    this._showClearButton(false)
    this.closeResults()

    this.dispatch("cleared", { bubbles: true })
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    handleListKeydown(event, {
      highlightedIndex: this.highlightedIndex,
      itemCount: this.filteredRoles.length,
      onHighlight: (index) => { this.highlightedIndex = index; this._renderResults() },
      onSelect: (index) => this._selectRole(this.filteredRoles[index]),
      onClose: () => this.closeResults(),
    })
  }

  closeResults() {
    this.resultsTarget.classList.add("hidden")
    this.isOpen = false
    this.highlightedIndex = -1
  }

  _selectRole(role) {
    this.inputTarget.value = role.name
    this.selectedRole = role
    this._showClearButton(true)
    this.closeResults()

    this.dispatch("selected", {
      bubbles: true,
      detail: { slug: role.slug, name: role.name },
    })
  }

  _openResults() {
    this.resultsTarget.classList.remove("hidden")
    this.isOpen = true
    this.highlightedIndex = -1
  }

  _showClearButton(show) {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", !show)
    }
  }

  _renderResults() {
    if (this.filteredRoles.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-sm text-gray-500">No matching roles found</div>
      `
      return
    }

    const html = this.filteredRoles.map((role, index) => {
      const isHighlighted = index === this.highlightedIndex
      const bgClass = isHighlighted ? "bg-cyan-50" : ""

      return `
        <div class="px-4 py-2.5 cursor-pointer hover:bg-gray-50 ${bgClass}"
             data-action="click->salary-lab-role-selector#select"
             data-index="${index}">
          <div class="text-sm font-medium text-gray-900">${this._escapeHtml(role.name)}</div>
          ${role.family ? `<div class="text-xs text-gray-500">${this._escapeHtml(role.family)}</div>` : ""}
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
  }

  _filterRoles(query) {
    const q = query.toLowerCase()
    return this.roles
      .filter(role => {
        if (role.name.toLowerCase().includes(q)) return true
        if (role.family && role.family.toLowerCase().includes(q)) return true
        if (role.alternates && role.alternates.some(a => a.toLowerCase().includes(q))) return true
        return false
      })
      .sort((a, b) => {
        const aName = a.name.toLowerCase()
        const bName = b.name.toLowerCase()
        const aStarts = aName.startsWith(q) ? 0 : 1
        const bStarts = bName.startsWith(q) ? 0 : 1
        if (aStarts !== bStarts) return aStarts - bStarts
        return (b.search_count || 0) - (a.search_count || 0)
      })
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
